import Vue from 'vue';
import _ from 'lodash';
import {
  contractorsSearch,
  contractorsSimpleSearch,
  contractorProfile,
  setContractorBlocking,
  contractorOrders,
  contractorTransactions,
  contractorUpdate,
  contractorDelete,
} from '@/api/contractors';
import {
  SET_CONTRACTORS,
  SET_SIMPLE_CONTRACTORS,
  SET_REQUESTS,
  SET_CONTRACTOR,
  SET_CONTRACTOR_BLOCKING,
  SET_CONTRACTOR_ORDERS,
  SET_CONTRACTOR_TRANSACTIONS,
  SET_DISABLED_CONTRACTORS, SET_CONTRACTOR_BALANCE_BY_ID,
} from './mutation-types';

export default {
  state: {
    data: {
      payload: [],
      total: null,
    },
    simple: {
      payload: [],
      total: null,
    },
    disabledContractors: {
      payload: [],
      total: null,
    },
    requests: {
      registration: {
        payload: [],
        total: null,
      },
      change: {
        payload: [],
        total: null,
      },
    },
    contractors: [],
    orders: {},
    transactions: {},
  },
  getters: {
    requests: (state) => (type) => _.get(state.requests, type),
    contractors: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return state.contractors.find((obj) => parseInt(obj.id, 10) === parseInt(id, 10)) || null;
      }
      return state.contractors;
    },
    orders: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.orders, id) || null;
      }
      return state.orders;
    },
    transactions: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.transactions, id) || null;
      }
      return state.transactions;
    },
  },
  actions: {
    ACT_FETCH_CONTRACTORS: async ({ commit, dispatch, rootGetters: getters }, {
      offset,
      limit,
      registered,
      verified,
      search,
      orderBy,
      regionId,
    }) => {
      dispatch('wait/start', 'fetch.contractors', { root: true });
      const params = {
        offset,
        limit,
        registered,
        verified,
        search,
        orderBy,
      };
      if (regionId) {
        params.regionId = regionId;
      }
      return contractorsSearch(params).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        const legalTypes = getters['settings/selectEnums']('legalType');
        commit(SET_CONTRACTORS, {
          data: data.map((contractor) => ({
            ...contractor,
            businessType: _.find(
              legalTypes,
              (legalType) => legalType.value === contractor.businessType,
            ),
          })),
          total,
        });
      }).finally(() => {
        dispatch('wait/end', 'fetch.contractors', { root: true });
      });
    },
    ACT_FETCH_DISABLED_CONTRACTORS: async ({ commit, dispatch }, {
      offset,
      limit,
      orderBy,
    }) => {
      dispatch('wait/start', 'fetch.disabled.contractors', { root: true });
      return contractorsSearch({
        offset,
        limit,
        disabled: 1,
        orderBy,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        commit(SET_DISABLED_CONTRACTORS, {
          data,
          total,
        });
      }).finally(() => {
        dispatch('wait/end', 'fetch.disabled.contractors', { root: true });
      });
    },
    ACT_SIMPLE_FETCH_CONTRACTORS: async ({
      commit,
      dispatch,
      rootGetters: getters,
      state,
    }, { query }) => {
      if (getters['wait/is']('fetch.contractors.simple')) {
        return Promise.reject();
      }
      dispatch('wait/start', 'fetch.contractors.simple', { root: true });
      const offset = -20;
      let referenceId = null;
      if (state.simple.payload.length) {
        const last = _.last(state.simple.payload);
        referenceId = _.get(last, 'id') || null;
      }
      return contractorsSimpleSearch({
        referenceId,
        offset,
        registered: 1,
        verified: 1,
        search: query,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        commit(SET_SIMPLE_CONTRACTORS, {
          data,
          total,
        });
      }).finally(() => {
        dispatch('wait/end', 'fetch.contractors.simple', { root: true });
      });
    },
    ACT_CLEAR_SIMPLE_CONTRACTORS: async ({ commit }) => {
      commit(SET_SIMPLE_CONTRACTORS, {
        data: [],
        total: null,
      });
      return Promise.resolve();
    },
    ACT_FETCH_REQUESTS: async ({ commit, dispatch, rootGetters: getters }, {
      key,
      offset,
      limit,
      registered,
      verified,
      orderBy,
      regionId,
      search,
    }) => {
      dispatch('wait/start', `fetch.requests.${key}`, { root: true });

      const params = {
        offset,
        limit,
        registered,
        verified,
        orderBy,
        search,
      };

      if (regionId) {
        params.regionId = regionId;
      }

      return contractorsSearch(params).then((response) => {
        if (response.data) {
          const data = _.get(response, 'data.result') || [];
          const total = _.get(response, 'data.total') || null;
          const legalTypes = getters['settings/selectEnums']('legalType');
          const payload = {
            key,
            data: data.map((contractor) => ({
              ...contractor,
              businessType: _.find(
                legalTypes,
                (legalType) => legalType.value === contractor.businessType,
              ),
            })),
            total,
          };
          commit(SET_REQUESTS, payload);
          return Promise.resolve(payload);
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `fetch.requests.${key}`, { root: true });
      });
    },
    ACT_FETCH_CONTRACTOR: async ({
      commit,
      dispatch,
      rootGetters: getters,
      rootState: state,
    }, id) => {
      dispatch('wait/start', `fetch.contractor.${id}`, { root: true });
      // eslint-disable-next-line consistent-return
      return contractorProfile(id).then((response) => {
        const payload = response.data;
        const legalTypes = getters['settings/selectEnums']('legalType');
        const taxSystems = getters['settings/selectEnums']('taxSystem');
        const regions = getters['settings/regions'];
        const districts = getters['settings/districts'];
        const businessType = _.get(payload, 'legalInfo.organization.businessType') || _.get(payload, 'legalInfo.type');
        if (response.data) {
          const country = _.get(payload, 'region.country.id');
          const region = _.get(payload, 'region.id');
          return Promise.all([
            dispatch('settings/ACT_FETCH_COUNTRIES', {}, { root: true }),
            dispatch('settings/ACT_FETCH_REGIONS', country, { root: true }),
            region ? dispatch('settings/ACT_FETCH_DISTRICTS', region, { root: true }) : Promise.resolve([]),
          ]).then(() => {
            const cityId = _.get(payload, 'cityId');
            const cityName = _.get(payload, 'city') || '';
            const foundCity = _.find(
              districts(region),
              (obj) => parseInt(obj.id, 10) === parseInt(cityId, 10),
            );
            const output = {
              ...payload,
              city: cityName,
              cityId: cityId || null,
              registrationComment: _.get(payload, 'registrationComment') || '',
              businessType: _.find(legalTypes, (obj) => obj.value === businessType),
              taxSystem: _.find(taxSystems, (obj) => obj.value === _.get(payload, 'legalInfo.organization.taxSystem')) || null,
              serviceCategoryItems: payload.serviceCategories || [],
              serviceCategories: _.map(payload.serviceCategories, (service) => service.id),
              location: {
                country: _.find(state.settings.countries, (obj) => obj.id === _.get(payload, 'region.country.id')),
                region: _.find(
                  regions(_.get(payload, 'region.country.id')),
                  (obj) => obj.id === _.get(payload, 'region.id'),
                ),
                city: foundCity || (cityName ? { id: cityId, name: cityName } : null),
              },
            };
            commit(SET_CONTRACTOR, {
              id,
              data: output,
            });
            return Promise.resolve(output);
          });
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `fetch.contractor.${id}`, { root: true });
      });
    },
    ACT_FETCH_CONTRACTOR_ORDERS: async ({ commit, dispatch }, {
      id,
      limit,
      offset,
      orderBy,
    }) => {
      dispatch('wait/start', `fetch.contractor.orders.${id}`, { root: true });
      return contractorOrders(id, {
        offset,
        limit,
        orderBy,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        if (data) {
          commit(SET_CONTRACTOR_ORDERS, {
            id,
            data: data.map((order) => ({
              ...order,
              name: `${_.get(order, 'firstServiceCategory.abbreviation')}-${order.id}`,
            })),
            total,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `fetch.contractor.orders.${id}`, { root: true });
      });
    },
    ACT_FETCH_CONTRACTOR_TRANSACTIONS: async ({ commit, dispatch }, {
      id,
      limit,
      offset,
    }) => {
      dispatch('wait/start', `fetch.contractor.transactions.${id}`, { root: true });
      return contractorTransactions(id, {
        limit,
        offset,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        if (data) {
          commit(SET_CONTRACTOR_TRANSACTIONS, {
            id,
            data,
            total,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `fetch.contractor.transactions.${id}`, { root: true });
      });
    },
    ACT_SET_CONTRACTOR_BLOCKING: async ({ commit, dispatch }, { id, reason }) => {
      dispatch('wait/start', `block.contractor.${id}`, { root: true });
      return setContractorBlocking(id, reason).then((response) => {
        if (response.status === 200) {
          commit(SET_CONTRACTOR_BLOCKING, {
            id,
            reason,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `block.contractor.${id}`, { root: true });
      });
    },
    ACT_CONTRACTOR_UPDATE: async (context, {
      id,
      payload,
    }) => { // eslint-disable-line arrow-body-style
      // eslint-disable-next-line arrow-body-style
      return contractorUpdate(id, payload).then((response) => {
        if (response.status === 200) {
          context.commit(SET_CONTRACTOR, response.data);
          return Promise.resolve();
        }
        return Promise.reject();
      });
    },
    ACT_DELETE_CONTRACTOR: async ({ dispatch }, id) => {
      dispatch('wait/start', `delete.contractor.${id}`, { root: true });
      return contractorDelete(id).then((response) => {
        if (response.status === 200) {
          return Promise.resolve();
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `delete.contractor.${id}`, { root: true });
      });
    },
  },
  mutations: {
    [SET_CONTRACTORS](state, { data, total }) {
      state.data.payload = data;
      state.data.total = total;
    },
    [SET_DISABLED_CONTRACTORS](state, { data, total }) {
      state.disabledContractors.payload = data;
      state.disabledContractors.total = total;
    },
    [SET_REQUESTS](state, { data, total, key }) {
      _.set(state, `requests.${key}.payload`, data);
      _.set(state, `requests.${key}.total`, total);
    },
    [SET_CONTRACTOR](state, { id, data }) {
      const payload = {
        ...data,
        id,
      };
      const foundIndex = _.findIndex(
        state.contractors,
        (obj) => parseInt(obj.id, 10) === parseInt(id, 10),
      );
      if (foundIndex > -1) {
        Vue.set(state.contractors, foundIndex, payload);
      } else {
        state.contractors.push(payload);
      }
    },
    [SET_CONTRACTOR_BLOCKING](state, { id, reason }) {
      state.contractors = state.contractors.map((obj) => {
        const data = obj;
        if (parseInt(obj.id, 10) === parseInt(id, 10)) {
          data.blockingReason = reason;
        }
        return data;
      });
    },
    [SET_CONTRACTOR_ORDERS](state, { id, data, total }) {
      Vue.set(state.orders, id, {
        data,
        total,
      });
    },
    [SET_CONTRACTOR_TRANSACTIONS](state, { id, data, total }) {
      Vue.set(state.transactions, id, {
        data,
        total,
      });
    },
    [SET_SIMPLE_CONTRACTORS](state, { data, total }) {
      if (data.length && total !== null) {
        state.simple = {
          payload: _.unionBy(state.simple.payload, data, (obj) => obj.id),
          total,
        };
      } else {
        state.simple = {
          payload: data,
          total,
        };
      }
    },
    [SET_CONTRACTOR_BALANCE_BY_ID](state, { id, balance }) {
      const foundIndex = _.findIndex(
        state.contractors,
        (obj) => parseInt(obj.id, 10) === parseInt(id, 10),
      );
      if (foundIndex > -1) {
        Vue.set(state.contractors, foundIndex, {
          ...state.contractors[foundIndex],
          balance,
        });
      }
    },
  },
  namespaced: true,
};
